import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { Box, List, ListItem, ListItemText, makeStyles, Menu, MenuItem, useTheme } from '@material-ui/core';
import { ArrowDropDown } from '@material-ui/icons';
import { USER_OPERATIONS } from 'helper';
import { RootState } from 'interfaces';
import { AppBarNavMenuItem } from './types';
import { APPBAR_MENU } from './menu';

const useStyles = makeStyles(() => ({
  menu: {
    overflow: 'auto',
    scrollbarWidth: 'none',

    '&::-webkit-scrollbar': {
      height: 0,
      width: 0,
    },

    '&:hover': {
      scrollbarWidth: 'thin',
    },

    '&:hover::-webkit-scrollbar': {
      height: 6,
      width: 6,
    },
  },
}));

const AppBarMenu = () => {
  const location = useLocation();
  const theme = useTheme();
  const operations = useSelector((state: RootState) => state.operations);
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [selectedMenuItem, setSelectedMenuItem] = useState<null | AppBarNavMenuItem>(null);

  const hasRequiredOperations = useCallback(
    (requiredOperations: AppBarNavMenuItem['operations']) => {
      if (!requiredOperations?.length) {
        return true;
      }
      return requiredOperations.some((operation) => {
        const operationName = USER_OPERATIONS[operation] || operation;
        return operations.includes(operationName);
      });
    },
    [operations],
  );

  const filteredMenuItems = useMemo(() => {
    return APPBAR_MENU.reduce<AppBarNavMenuItem[]>((acc, item) => {
      const children = item.children;

      if (!children || children.length === 0) {
        if (hasRequiredOperations(item.operations)) {
          acc.push({ ...item, children });
        }
        return acc;
      }

      if (!hasRequiredOperations(item.operations)) {
        return acc;
      }

      const accessibleChildren = children.filter((child) => hasRequiredOperations(child.operations));
      if (!accessibleChildren.length && !item.path) {
        return acc;
      }

      acc.push({ ...item, children: accessibleChildren });
      return acc;
    }, []);
  }, [hasRequiredOperations]);

  const isExternalPath = useCallback((path: string) => {
    return /^https?:\/\//.test(path);
  }, []);

  function handleMenuItemClick(event: React.MouseEvent<HTMLElement>, item: AppBarNavMenuItem) {
    // Если есть дочерние элементы - открываем меню
    if (item.children?.length) {
      setAnchorEl(event.currentTarget);
      setSelectedMenuItem(item);
      return;
    }

    // Если это внешняя ссылка, браузер откроет её автоматически через href
    // Для внутренних ссылок Link из react-router-dom обработает навигацию
    setSelectedMenuItem(item);
  }

  function handleClickSubMenuItem(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(null);
    setTimeout(() => setSelectedMenuItem(null), theme.transitions.duration.short); // чтобы не мигало при быстром клике
  }

  return (
    <>
      <Box sx={{ flexGrow: 1 }} className={classes.menu}>
        <List disablePadding dense style={{ display: 'inline-flex', alignItems: 'center' }} component="nav">
          {filteredMenuItems.map((item) => {
            const isSelected = item.path
              ? location.pathname === item.path
              : Boolean(item.children?.some((child) => child.path === location.pathname));

            const hasPath = Boolean(item.path);
            const hasChildren = Boolean(item.children?.length);
            const isExternal = hasPath && isExternalPath(item.path!);

            // Определяем правильный компонент и props для ListItem
            const listItemProps: any = {
              selected: isSelected,
              button: true,
              key: item.name,
              style: { borderRadius: 8 },
            };

            // Если есть path и нет детей - делаем ссылкой
            if (hasPath && !hasChildren) {
              if (isExternal) {
                // Внешняя ссылка
                listItemProps.component = 'a';
                listItemProps.href = item.path;
                listItemProps.target = '_blank';
                listItemProps.rel = 'noopener noreferrer';
              } else {
                // Внутренняя ссылка через react-router
                listItemProps.component = Link;
                listItemProps.to = item.path;
              }
              listItemProps.onClick = (event: React.MouseEvent<HTMLElement>) => handleMenuItemClick(event, item);
            } else {
              // Если есть дети или нет path - обычный button
              listItemProps.onClick = (event: React.MouseEvent<HTMLElement>) => handleMenuItemClick(event, item);
            }

            return (
              <ListItem {...listItemProps}>
                <ListItemText primary={item.name} primaryTypographyProps={{ noWrap: true }} />
                {hasChildren && <ArrowDropDown />}
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setAnchorEl(null)}
      >
        {(selectedMenuItem?.children || []).map((item) => {
          const isSelected = item.path ? location.pathname === item.path : false;
          const hasPath = Boolean(item.path);
          const isExternal = hasPath && isExternalPath(item.path!);

          const menuItemProps: any = {
            dense: true,
            selected: isSelected,
            key: item.name,
            onClick: handleClickSubMenuItem,
          };

          // Если есть path - делаем ссылкой
          if (hasPath) {
            if (isExternal) {
              // Внешняя ссылка
              menuItemProps.component = 'a';
              menuItemProps.href = item.path;
              menuItemProps.target = '_blank';
              menuItemProps.rel = 'noopener noreferrer';
            } else {
              // Внутренняя ссылка через react-router
              menuItemProps.component = Link;
              menuItemProps.to = item.path;
            }
          }

          return (
            <MenuItem {...menuItemProps}>
              <ListItemText primary={item.name} primaryTypographyProps={{ noWrap: true }} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
export default AppBarMenu;
